/* @flow */

import type { RouterHistory as History, Location } from "react-router";

import React from "react";
import { Route, Switch } from "react-router";
import Redirect from "@crossroads/react-router-redirect";
import CmsPageView from "components/CmsPageView";
import {
  RouteData, OrderData, HomeData, BrandData, SearchData,
  CmsPageData, PopularData, AllProductsData, AdventCalendarData,
  CmsBlockData,
} from "data";
import { PAGE_SIZE } from "effects/route";
import { StoreInfoContext } from "entrypoint/shared";
import { useData } from "crustate/react";
import { useTranslate } from "@awardit/react-use-translate";
import App from "components/App";
import LoadingView from "components/LoadingView";
import SearchView from "components/SearchView";
import { ProductListViewWrapper, ProductListView, HintProductListView, AdventCalendarView } from "@crossroads/shop-views";
import ProductView, { HintProductView } from "components/ProductView";
import HomeView from "components/HomeView";
import SuccessView from "components/SuccessView";
import CategorySection from "components/CategorySection";
import CheckoutCart from "components/Checkout/Cart";
import CheckoutOverview from "components/Checkout/Overview";
import NotFoundView from "components/NotFoundView";
import ProductCard from "components/ProductCard";
import { parseParams } from "helpers/location-search-string";
import { locationWithDefaults } from "helpers/use-update-product-list";
import { useUi } from "helpers/ui";

type RoutesProps = {
  location: Location,
  history: History,
};

type RouteViewProps = {
  location: Location,
  history: History,
  showMiniBanner: boolean,
};

const RouteView = ({ location, history, showMiniBanner }: RouteViewProps): React$Node => {
  const t = useTranslate();
  const data = useData(RouteData);
  const homeData = useData(HomeData);
  const onAdventCalendarCategoryPath = location.pathname.includes(t("ADVENT_CALENDAR.CATEGORY_URL"));

  if (data.state === "LOADING") {
    if (data.hint) {
      switch (data.hint.type) {
        case "product":
          return <HintProductView product={data.hint.product} />;
        case "category":
          return (
            <HintProductListView
              title={data.hint.category.name}
              StoreInfoContext={StoreInfoContext}
            />
          );
        case "cms_page":
          return <CmsPageView cmsPage={data.hint.cmsPage} />;
        default:
      }
    }

    return <LoadingView />;
  }

  if (!data.route) {
    // TODO: Check error msg
    return <NotFoundView />;
  }

  switch (data.route.type) {
    case "product":
      return (
        <ProductView product={data.route.product} />
      );
    case "category":
      const title = onAdventCalendarCategoryPath &&
      data.route.category.heroHeading !== null &&
      data.route.category.heroHeading !== undefined ?
        data.route.category.heroHeading : data.route.category.name;
      return (
        <ProductListView
          hasFilters
          hasPagination
          hasAdventCalendarHero
          hasAlternativeHeroLayout
          variant="category"
          history={history}
          category={data.route.category}
          home={homeData.state === "LOADED" ? homeData.data : {}}
          productList={data.route.category.products}
          title={title}
          isUpdating={data.state === "UPDATING"}
          pageSize={PAGE_SIZE}
          StoreInfoContext={StoreInfoContext}
          uiContext={useUi}
          currentBreadcrumb={data.route.category.name}
          productsLink={location.pathname}
          ProductCardComponent={ProductCard}
          filterbarOffsetClass={showMiniBanner ? "miniBannerOffset" : ""}
          CampaignCardData={CmsBlockData}
          afterContent={<CategorySection useWrapper />}
          onAdventCalendarPath={t("ADVENT_CALENDAR.CATEGORY_URL")}
        />
      );
    case "cms_page":
      return <CmsPageView cmsPage={data.route.cmsPage} />;
    case "redirect":
      return <Redirect to={data.route.url} />;
    default:
      return null;
  }
};

const Routes = ({ location, history }: RoutesProps): React$Node => {
  const t = useTranslate();
  const miniBannerData = useData(CmsBlockData);
  const showMiniBanner = miniBannerData.state === "LOADED" &&
    miniBannerData.data !== null && miniBannerData.data.content !== "";

  return (
    <Switch>
      <App location={location} history={history} showMiniBanner={showMiniBanner}>
        <Switch>
          <Route
            exact path="/" render={() => (
              <HomeData.Provider>
                <CmsBlockData.Provider identifier="offerings">
                  <HomeView />
                </CmsBlockData.Provider>
              </HomeData.Provider>
            )} />

          <Route
            exact
            path={t("URL.CHECKOUT_CART")}
            component={CheckoutCart}
          />

          <Route
            exact
            path={t("URL.CHECKOUT_OVERVIEW")}
            render={() => (
              <CmsPageData.Provider url="terms">
                <CheckoutOverview />
              </CmsPageData.Provider>
            )}
          />

          <Route
            path={t("URL.CHECKOUT_SUCCESS")}
            render={() => (
              <OrderData.Provider>
                <SuccessView />
              </OrderData.Provider>
            )}
          />

          <Route
            path="/brand/:brand"
            render={({ match, location: { search } }) => {
              const { page } = parseParams(search);
              const brand = match.params.brand || "";

              return (
                <HomeData.Provider>
                  <BrandData.Provider
                    location={locationWithDefaults(location)}
                    name={brand}
                    brand={decodeURIComponent(brand)}
                    page={page}
                  >
                    <CmsBlockData.Provider identifier="campaign-card">
                      <ProductListViewWrapper
                        variant="brands"
                        data={BrandData}
                        pageSize={PAGE_SIZE}
                        uiContext={useUi}
                        StoreInfoContext={StoreInfoContext}
                        filterbarOffsetClass={showMiniBanner ? "miniBannerOffset" : ""}
                        ProductCardComponent={ProductCard}
                        afterContent={<CategorySection useWrapper />}
                        CampaignCardData={CmsBlockData}
                      />
                    </CmsBlockData.Provider>
                  </BrandData.Provider>
                </HomeData.Provider>
              );
            }} />

          <Route
            exact path="/popular-products"
            render={({ location: { search } }) => {
              const { page } = parseParams(search);

              return (
                <HomeData.Provider>
                  <PopularData.Provider
                    location={locationWithDefaults(location)}
                    page={page}
                  >
                    <CmsBlockData.Provider identifier="campaign-card">
                      <ProductListViewWrapper
                        variant="popular"
                        data={PopularData}
                        pageSize={PAGE_SIZE}
                        uiContext={useUi}
                        StoreInfoContext={StoreInfoContext}
                        filterbarOffsetClass={showMiniBanner ? "miniBannerOffset" : ""}
                        title={t("POPULAR.POPULAR_PRODUCTS")}
                        ProductCardComponent={ProductCard}
                        afterContent={<CategorySection useWrapper />}
                        CampaignCardData={CmsBlockData}
                      />
                    </CmsBlockData.Provider>
                  </PopularData.Provider>
                </HomeData.Provider>
              );
            }} />

          <Route
            exact path="/all-products"
            render={({ location: { search } }) => {
              const { page } = parseParams(search);

              return (
                <HomeData.Provider>
                  <AllProductsData.Provider
                    location={locationWithDefaults(location)}
                    page={page}
                  >
                    <CmsBlockData.Provider identifier="campaign-card">
                      <ProductListViewWrapper
                        variant="allproducts"
                        data={AllProductsData}
                        pageSize={PAGE_SIZE}
                        uiContext={useUi}
                        StoreInfoContext={StoreInfoContext}
                        filterbarOffsetClass={showMiniBanner ? "miniBannerOffset" : ""}
                        title={t("ALL_PRODUCTS.TITLE")}
                        ProductCardComponent={ProductCard}
                        afterContent={<CategorySection useWrapper />}
                        CampaignCardData={CmsBlockData}
                      />
                    </CmsBlockData.Provider>
                  </AllProductsData.Provider>
                </HomeData.Provider>
              );
            }} />

          <Route
            exact
            path="/julkalender"
            render={() => {
              return (
                <HomeData.Provider>
                  <AdventCalendarView
                    hasAdventCalendarHero
                    hasAlternativeLayout
                    calendarData={AdventCalendarData}
                    homeData={HomeData}
                    LoadingView={LoadingView}
                  />
                </HomeData.Provider>
              );
            }} />

          <Route
            path="/search/:query"
            render={({ match, location: { search } }) => {
              const { page } = parseParams(search);

              const query = decodeURIComponent(match.params.query || "");

              return (
                <SearchData.Provider
                  location={locationWithDefaults(location)}
                  page={page}
                  query={query}
                  name={`search_${query}`}
                >
                  <SearchView query={query} showMiniBanner={showMiniBanner} />
                </SearchData.Provider>
              );
            }}
          />

          <Route
            path="/(.+)"
            render={({ location: { pathname, search, state }, history }) => {
              const { page } = parseParams(search);

              return (
                <HomeData.Provider>
                  <RouteData.Provider
                    location={locationWithDefaults(location)}
                    url={pathname}
                    name={`route_${pathname}`}
                    page={page}
                    hint={state && state.hint}
                  >
                    <CmsBlockData.Provider identifier="campaign-card">
                      <RouteView
                        location={location}
                        history={history}
                        showMiniBanner={showMiniBanner}
                      />
                    </CmsBlockData.Provider>
                  </RouteData.Provider>
                </HomeData.Provider>
              );
            }} />
        </Switch>
      </App>
    </Switch>
  );
};

const AppRoutes = (): React$Node => <Route component={Routes} />;

export default AppRoutes;
